export interface InputState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  sprint: boolean;
  interact: boolean; // New interaction key
  cancel: boolean; // Cancel/Escape key
  toggleColliders: boolean; // Toggle collider visibility
  toggleInventory: boolean; // Toggle inventory display
  // Smooth directional values for joystick (range: -1 to 1)
  horizontalAxis: number;
  verticalAxis: number;
}

export class InputManager {
  private keys: { [key: string]: boolean } = {};
  private inputState: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    sprint: false,
    interact: false,
    cancel: false,
    toggleColliders: false,
    toggleInventory: false,
    horizontalAxis: 0,
    verticalAxis: 0,
  };

  private joystickActive = false;
  private joystickCenter = { x: 0, y: 0 };
  private joystickRadius = 50;
  private lastInteractState = false;
  private lastUpState = false;
  private lastDownState = false;
  private lastCancelState = false;
  private lastToggleCollidersState = false;
  private lastToggleInventoryState = false;
  private mobileControls: HTMLElement | null = null;
  private pixiUICheckCallback: ((x: number, y: number) => boolean) | null = null;
  private isMobileDevice: boolean = false;

  private static instance: InputManager;

  constructor() {
    InputManager.instance = this;
    this.checkMobileDevice();
    this.setupKeyboardControls();
    this.setupMobileControls();
  }

  private checkMobileDevice(): void {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Simple mobile detection based on UA and touch capability
    if (/android/i.test(userAgent) || /iPad|iPhone|iPod/i.test(userAgent)) {
      this.isMobileDevice = true;
    } else if (isTouch && window.innerWidth < 1024) {
      // Small touch screens also count as mobile
      this.isMobileDevice = true;
    } else {
      this.isMobileDevice = false;
    }
    
    console.log(`InputManager: Device detected as ${this.isMobileDevice ? 'Mobile' : 'Desktop'}`);
  }

  public isMobile(): boolean {
    return this.isMobileDevice;
  }

  public static getInstance(): InputManager {
    if (!InputManager.instance) {
       console.warn("InputManager accessed before initialization, creating new instance");
       new InputManager();
    }
    return InputManager.instance;
  }

  private setupKeyboardControls(): void {
    window.addEventListener('keydown', e => {
      this.keys[e.code.toLowerCase()] = true;
      this.updateInputState();
    });

    window.addEventListener('keyup', e => {
      this.keys[e.code.toLowerCase()] = false;
      this.updateInputState();
    });
  }

  private setupMobileControls(): void {
    const mobileControls = document.getElementById('mobileControls');
    const joystickKnob = document.getElementById('joystickKnob');

    if (!mobileControls || !joystickKnob) return;

    // Store references to mobile controls for visibility control
    this.mobileControls = mobileControls;

    // Joystick Logic (Mobile Only)
    if (this.isMobileDevice) {
       this.setupJoystick(mobileControls, joystickKnob);
    } else {
       mobileControls.style.display = 'none';
    }

    // Interaction Button Logic (Universal)
    this.setupInteractionButton();
  }

  private setupJoystick(mobileControls: HTMLElement, joystickKnob: HTMLElement): void {
    const handleStart = (clientX: number, clientY: number) => {
      this.joystickActive = true;
      
      // Fixed Joystick: Use the existing element position as center
      const rect = mobileControls.getBoundingClientRect();
      this.joystickCenter = { 
        x: rect.left + rect.width / 2, 
        y: rect.top + rect.height / 2 
      };
      
      mobileControls.classList.add('active');

      this.updateJoystick(clientX, clientY, joystickKnob);
    };

    const handleMove = (clientX: number, clientY: number) => {
      if (this.joystickActive) {
        this.updateJoystick(clientX, clientY, joystickKnob);
      }
    };

    const handleEnd = () => {
      this.joystickActive = false;
      joystickKnob.style.transform = 'translate(0, 0)';
      mobileControls.classList.remove('active');

      this.inputState = {
        left: false,
        right: false,
        up: false,
        down: false,
        sprint: false,
        interact: false,
        cancel: false,
        toggleColliders: false,
        toggleInventory: false,
        horizontalAxis: 0,
        verticalAxis: 0,
      };
      // Remove sprint visual feedback
      if (this.mobileControls) {
        this.mobileControls.classList.remove('sprint');
      }
    };

    // Touch events - listen on document for dynamic joystick
    document.addEventListener('touchstart', e => {
      const touch = e.touches[0];
      
      // Check if touching a PIXI UI element
      if (this.pixiUICheckCallback?.(touch.clientX, touch.clientY)) return;

      e.preventDefault();
      handleStart(touch.clientX, touch.clientY);
    });

    document.addEventListener('touchmove', e => {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    });

    document.addEventListener('touchend', e => {
      e.preventDefault();
      handleEnd();
    });
    
    // Mouse events for testing on desktop - listen on document
    document.addEventListener('mousedown', e => {
      // Check if clicking a PIXI UI element
      if (this.pixiUICheckCallback?.(e.clientX, e.clientY)) return;

      handleStart(e.clientX, e.clientY);
    });

    document.addEventListener('mousemove', e => {
      handleMove(e.clientX, e.clientY);
    });

    document.addEventListener('mouseup', () => {
      handleEnd();
    });
  }

  private setupInteractionButton(): void {
    const interactionBtn = document.getElementById('interactionBtn');
    if (interactionBtn) {
      const triggerInteract = (active: boolean) => {
        this.inputState.interact = active;
      };
      
      interactionBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent joystick from picking this up
        triggerInteract(true);
      });
      interactionBtn.addEventListener('touchend', (e) => {
        e.preventDefault();
        triggerInteract(false);
      });
      interactionBtn.addEventListener('mousedown', (e) => {
         e.preventDefault();
         e.stopPropagation();
         triggerInteract(true);
      });
      interactionBtn.addEventListener('mouseup', (e) => {
         e.preventDefault();
         triggerInteract(false);
      });
    }
  }

  // Placeholder to keep original function signature just in case, but essentially replacing content
  private _unused(): void { 
  
  }

  private updateJoystick(clientX: number, clientY: number, knob: HTMLElement): void {
    const dx = clientX - this.joystickCenter.x;
    const dy = clientY - this.joystickCenter.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const clampedDistance = Math.min(distance, this.joystickRadius);
    const angle = Math.atan2(dy, dx);

    const knobX = Math.cos(angle) * clampedDistance;
    const knobY = Math.sin(angle) * clampedDistance;

    knob.style.transform = `translate(${knobX}px, ${knobY}px)`;

    // Calculate smooth directional values (-1 to 1)
    this.inputState.horizontalAxis = knobX / this.joystickRadius;
    this.inputState.verticalAxis = knobY / this.joystickRadius;

    // Update discrete input state based on joystick position (for backward compatibility)
    const threshold = 20;
    this.inputState.left = knobX < -threshold;
    this.inputState.right = knobX > threshold;
    this.inputState.up = knobY < -threshold;
    this.inputState.down = knobY > threshold;

    // Sprint activation: when joystick is pushed beyond the circle boundary with margin
    const sprintThreshold = this.joystickRadius * 1.7; // 10% margin beyond the circle
    this.inputState.sprint = distance >= sprintThreshold;

    // Visual feedback for sprint state
    if (this.mobileControls) {
      if (this.inputState.sprint) {
        this.mobileControls.classList.add('sprint');
      } else {
        this.mobileControls.classList.remove('sprint');
      }
    }
  }

  private updateInputState(): void {
    this.inputState.left = this.keys.keya || this.keys.arrowleft || false;
    this.inputState.right = this.keys.keyd || this.keys.arrowright || false;
    this.inputState.up = this.keys.keyw || this.keys.arrowup || false;
    this.inputState.down = this.keys.keys || this.keys.arrowdown || false;
    this.inputState.sprint = this.keys.shiftleft || this.keys.shiftright || false;
    this.inputState.interact = this.keys.keye || this.keys.space || false;
    this.inputState.cancel = this.keys.escape || false;
    this.inputState.toggleColliders = this.keys.keyc || false;
    this.inputState.toggleInventory = this.keys.keyi || false;

    // Update axis values for keyboard input (digital to analog conversion)
    this.inputState.horizontalAxis = 0;
    this.inputState.verticalAxis = 0;

    if (this.inputState.left) this.inputState.horizontalAxis -= 1;
    if (this.inputState.right) this.inputState.horizontalAxis += 1;
    if (this.inputState.up) this.inputState.verticalAxis -= 1;
    if (this.inputState.down) this.inputState.verticalAxis += 1;
  }

  public getInputState(): InputState {
    return { ...this.inputState };
  }

  public wasInteractPressed(): boolean {
    const currentInteractState = this.inputState.interact;
    const wasPressed = currentInteractState && !this.lastInteractState;
    this.lastInteractState = currentInteractState;
    return wasPressed;
  }

  public wasUpPressed(): boolean {
    const currentUpState = this.inputState.up;
    const wasPressed = currentUpState && !this.lastUpState;
    this.lastUpState = currentUpState;
    return wasPressed;
  }

  public wasDownPressed(): boolean {
    const currentDownState = this.inputState.down;
    const wasPressed = currentDownState && !this.lastDownState;
    this.lastDownState = currentDownState;
    return wasPressed;
  }

  public wasCancelPressed(): boolean {
    const currentCancelState = this.inputState.cancel;
    const wasPressed = currentCancelState && !this.lastCancelState;
    this.lastCancelState = currentCancelState;
    return wasPressed;
  }

  public wasToggleCollidersPressed(): boolean {
    const currentToggleCollidersState = this.inputState.toggleColliders;
    const wasPressed = currentToggleCollidersState && !this.lastToggleCollidersState;
    this.lastToggleCollidersState = currentToggleCollidersState;
    return wasPressed;
  }

  public wasToggleInventoryPressed(): boolean {
    const currentToggleInventoryState = this.inputState.toggleInventory;
    const wasPressed = currentToggleInventoryState && !this.lastToggleInventoryState;
    this.lastToggleInventoryState = currentToggleInventoryState;
    return wasPressed;
  }

  public setMobileControlsVisible(visible: boolean): void {
    if (!this.isMobileDevice) return;

    if (this.mobileControls) {
      if (visible) {
        this.mobileControls.classList.remove('hidden');
        this.mobileControls.style.display = 'flex'; 
      } else {
        this.mobileControls.classList.add('hidden');
      }
    }
  }

  public setInteractButtonVisible(visible: boolean): void {
     const btn = document.getElementById('interactionBtn');
     if (btn) {
       if (visible) btn.classList.add('visible');
       else btn.classList.remove('visible');
     }
  }

  public setInteractButtonActive(active: boolean): void {
     const btn = document.getElementById('interactionBtn');
     if (btn) {
       if (active) btn.classList.add('can-interact');
       else btn.classList.remove('can-interact');
     }
  }

  /**
   * Set a callback function to check if a position is over a PIXI UI element
   * This is used to prevent joystick activation when clicking on UI elements
   */
  public setPixiUICheckCallback(callback: (x: number, y: number) => boolean): void {
    this.pixiUICheckCallback = callback;
  }
}
